import type { Observable } from "rxjs";
import { concat as observableConcat, of as observableOf, throwError as observableThrowError } from "rxjs";

import { catchError, filter, map } from "rxjs/operators";

import type { IResponse, IResponseWithLoading } from "../../Models";
import { zipWithTimer } from "./zipWithTimer";

type ExtendWithLoadingAndFail = <T, TExtraReponseProps = Record<string, any>>(
    observable: Observable<(IResponse<T> & TExtraReponseProps) | (IResponseWithLoading<T> & TExtraReponseProps)>,
    minimumLoadingDelay?: number | Date,
) => Observable<IResponseWithLoading<T> & TExtraReponseProps>;

export const extendWithLoadingAndFail: ExtendWithLoadingAndFail = <T, TExtraReponseProps = Record<string, any>>(
    observable: Observable<(IResponse<T> & TExtraReponseProps) | (IResponseWithLoading<T> & TExtraReponseProps)>,
    minimumLoadingDelay: number | Date = 1000,
) => {
    const initialLoadingEvent: IResponseWithLoading<T & TExtraReponseProps> = {
        isLoading: true,
        isPartiallyLoading: true,
    };

    return observableConcat(
        observableOf(initialLoadingEvent),
        observable.pipe(
            zipWithTimer(minimumLoadingDelay),
            filter((response) => (response as IResponseWithLoading<T> & TExtraReponseProps).isLoading !== true),
            map((response) => {
                const responseWithLoading = response as IResponseWithLoading<T> & TExtraReponseProps;
                responseWithLoading.isLoading = false;
                responseWithLoading.isPartiallyLoading = false;
                responseWithLoading.isPartiallySuccessful = responseWithLoading.isSuccessful;
                return responseWithLoading;
            }),
            catchError((error: IResponse<any>) => {
                const errorWithLoading = error as IResponseWithLoading<T> & TExtraReponseProps;
                errorWithLoading.isLoading = false;
                return observableThrowError(errorWithLoading);
            }),
        ),
    );
};
